import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { request } from "../lib/api";
import { useAttribution } from "../lib/useAttribution";
import { AttributionCell } from "../components/Attribution";
import { formatDate, formatTime, fromPkInput, toPkInput } from "../lib/datetime";
import type {
  CreatedResponse,
  Notice,
  PDDay,
  PDDaysResponse,
  PDSession,
  PDSessionsResponse,
  StructureItem,
  StructureResponse,
  UsersResponse,
} from "../types";
import {
  Badge,
  Card,
  ConfirmModal,
  EmptyState,
  ErrorState,
  InlineNotice,
  LoadingState,
  Table,
  dangerButtonClass,
  errorMessage,
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "../components/ui";

const statusTone = {
  scheduled: "neutral",
  in_progress: "success",
  ended: "danger",
} as const;

export function PDSessions() {
  const [sessions, setSessions] = useState<PDSession[] | null>(null);
  const attribution = useAttribution(
    "pd-sessions",
    (sessions ?? []).map((session) => session.id)
  );
  const [listError, setListError] = useState("");
  const [message, setMessage] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<PDSession | null>(null);
  const [confirmItem, setConfirmItem] = useState<PDSession | null>(null);
  const [deletingID, setDeletingID] = useState<string | null>(null);

  const [centers, setCenters] = useState<StructureItem[]>([]);
  const [rooms, setRooms] = useState<StructureItem[]>([]);
  const [cohorts, setCohorts] = useState<StructureItem[]>([]);
  const [institutes, setInstitutes] = useState<StructureItem[]>([]);
  const [pdDays, setPDDays] = useState<PDDay[]>([]);
  const [facilitators, setFacilitators] = useState<UsersResponse["users"]>([]);

  const [centerID, setCenterID] = useState("");
  const [cohortID, setCohortID] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedFacilitators, setSelectedFacilitators] = useState<string[]>([]);
  const [selectedSchools, setSelectedSchools] = useState<string[]>([]);
  const [formCenterID, setFormCenterID] = useState("");
  const [formCohortID, setFormCohortID] = useState("");

  // A PD day's roster is exactly the active teachers at the schools ticked
  // on the form. Before saving, warn when none of those schools belong to
  // the chosen cohort — almost always a mis-pick. After saving, the list
  // uses the server's real expected_teachers count instead of re-deriving.
  const rosterGap = useCallback(
    (forSchoolIDs: string[], forCohortID: string): string | null => {
      if (!forSchoolIDs.length || !forCohortID) return null;
      const inCohort = institutes.filter(
        (school) => forSchoolIDs.includes(school.id) && school.cohort_ids?.includes(forCohortID)
      );
      if (inCohort.length === 0) {
        return "None of the ticked schools are linked to this cohort — check you picked the right schools, or link them on the Schools page.";
      }
      return null;
    },
    [institutes]
  );

  const load = useCallback(() => {
    const query = new URLSearchParams();
    if (centerID) query.set("center_id", centerID);
    if (cohortID) query.set("cohort_id", cohortID);
    if (statusFilter) query.set("status", statusFilter);
    const suffix = query.toString() ? `?${query}` : "";
    request<PDSessionsResponse>(`/api/admin/pd-sessions${suffix}`)
      .then((data) => {
        setSessions(data.pd_sessions);
        setListError("");
      })
      .catch((cause) => setListError(errorMessage(cause)));
  }, [centerID, cohortID, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    request<StructureResponse>("/api/admin/structure/centers")
      .then((data) => setCenters(data.items))
      .catch(() => setCenters([]));
    request<StructureResponse>("/api/admin/structure/rooms")
      .then((data) => setRooms(data.items))
      .catch(() => setRooms([]));
    request<StructureResponse>("/api/admin/structure/cohorts")
      .then((data) => setCohorts(data.items))
      .catch(() => setCohorts([]));
    request<StructureResponse>("/api/admin/structure/institutes")
      .then((data) => setInstitutes(data.items))
      .catch(() => setInstitutes([]));
    request<PDDaysResponse>("/api/admin/pd-days")
      .then((data) => setPDDays(data.pd_days || []))
      .catch(() => setPDDays([]));
    request<UsersResponse>("/api/admin/users")
      .then((data) => setFacilitators(data.users.filter((user) => user.role === "facilitator")))
      .catch(() => setFacilitators([]));
  }, []);

  function startEdit(session: PDSession) {
    setEditing(session);
    setSelectedFacilitators(session.facilitators.map((person) => person.id));
    setFormCenterID(session.center_id);
    setFormCohortID(session.cohort_id);
    setSelectedSchools(session.schools.map((school) => school.id));
    setMessage(null);
  }

  // Picking a centre pre-ticks that school, since the host school's own
  // teachers almost always attend; it can still be unticked.
  function chooseCentre(centreID: string) {
    setFormCenterID(centreID);
    if (centreID && !selectedSchools.includes(centreID)) {
      setSelectedSchools((current) => [...current, centreID]);
    }
  }

  function toggle(list: string[], id: string, on: boolean): string[] {
    return on ? [...list, id] : list.filter((entry) => entry !== id);
  }

  function cancelEdit() {
    setEditing(null);
    setSelectedFacilitators([]);
    setSelectedSchools([]);
    setFormCenterID("");
    setFormCohortID("");
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedSchools.length) {
      setMessage({ tone: "error", text: "Choose at least one school whose teachers attend." });
      return;
    }
    if (!selectedFacilitators.length) {
      setMessage({ tone: "error", text: "Assign at least one facilitator." });
      return;
    }
    setBusy(true);
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form)) as Record<string, string>;
    const payload = {
      cohort_id: data.cohort_id,
      center_id: data.center_id,
      pd_day_id: data.pd_day_id,
      room_id: data.room_id,
      facilitator_ids: selectedFacilitators,
      institute_ids: selectedSchools,
      start: fromPkInput(data.start),
      end: fromPkInput(data.end),
    };
    try {
      if (editing) {
        await request(`/api/admin/pd-sessions/${editing.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        setMessage({ tone: "success", text: "Updated PD day." });
        cancelEdit();
      } else {
        const result = await request<CreatedResponse>("/api/admin/pd-sessions", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        setMessage({ tone: "success", text: `Scheduled PD day: ${result.id}` });
        setSelectedFacilitators([]);
        setSelectedSchools([]);
        setFormCenterID("");
        setFormCohortID("");
      }
      form.reset();
      load();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }

  async function remove(session: PDSession) {
    setDeletingID(session.id);
    setMessage(null);
    try {
      await request(`/api/admin/pd-sessions/${session.id}`, { method: "DELETE" });
      setMessage({ tone: "success", text: "Deleted PD day." });
      if (editing?.id === session.id) cancelEdit();
      load();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setDeletingID(null);
      setConfirmItem(null);
    }
  }

  // Rooms belong to a centre, so only that centre's rooms can be chosen.
  const roomsForCentre = formCenterID
    ? rooms.filter((room) => room.center_id === formCenterID)
    : [];
  const formRosterGap = useMemo(
    () => rosterGap(selectedSchools, formCohortID),
    [rosterGap, selectedSchools, formCohortID]
  );

  return (
    <div>
      <Card
        title={editing ? "Edit PD day" : "Schedule a PD day"}
        text="Schedules a PD day at one centre, on a date, in a room, with the facilitators who will run it. The same PD day can be scheduled at several centres."
      >
        <form className="grid gap-5 sm:grid-cols-2" onSubmit={submit} key={editing?.id || "new"}>
          <label className={labelClass}>
            Cohort
            <select
              className={inputClass}
              name="cohort_id"
              required
              value={formCohortID}
              onChange={(event) => setFormCohortID(event.target.value)}
            >
              <option value="">Select cohort</option>
              {cohorts.map((cohort) => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.name}
                </option>
              ))}
            </select>
          </label>

          <label className={labelClass}>
            PD day (its modules are the content)
            <select
              className={inputClass}
              name="pd_day_id"
              required
              disabled={!formCohortID}
              defaultValue={editing?.pd_day_id || ""}
              key={formCohortID}
            >
              <option value="">{formCohortID ? "Select PD day" : "Select a cohort first"}</option>
              {pdDays
                .filter((day) => day.cohort_id === formCohortID && day.status !== "archived")
                .map((day) => (
                  <option key={day.id} value={day.id}>
                    {day.title} · {day.module_count} {day.module_count === 1 ? "module" : "modules"}
                    {day.status === "published" ? "" : ` (${day.status})`}
                  </option>
                ))}
            </select>
          </label>

          <label className={labelClass}>
            Centre
            <select
              className={inputClass}
              name="center_id"
              required
              value={formCenterID}
              onChange={(event) => chooseCentre(event.target.value)}
            >
              <option value="">Select centre</option>
              {centers.map((centre) => (
                <option key={centre.id} value={centre.id}>
                  {centre.name}
                </option>
              ))}
            </select>
          </label>

          {formRosterGap && (
            <p className="flex items-start gap-1.5 rounded-md bg-warning-bg px-3 py-2 text-sm text-warning-text sm:col-span-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              {formRosterGap}
            </p>
          )}

          <label className={labelClass}>
            Room (optional)
            <select className={inputClass} name="room_id" defaultValue={editing?.room_id || ""}>
              <option value="">{formCenterID ? "No room" : "Choose a centre first"}</option>
              {roomsForCentre.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name}
                </option>
              ))}
            </select>
          </label>

          <label className={labelClass}>
            Starts
            <input
              className={inputClass}
              name="start"
              type="datetime-local"
              required
              defaultValue={editing ? toPkInput(editing.start) : ""}
            />
          </label>

          <label className={labelClass}>
            Ends
            <input
              className={inputClass}
              name="end"
              type="datetime-local"
              required
              defaultValue={editing ? toPkInput(editing.end) : ""}
            />
          </label>

          <fieldset className="sm:col-span-2">
            <legend className="mb-1 text-sm font-medium text-content-primary">
              Schools attending (at least one)
            </legend>
            <p className="mb-2 text-xs text-content-secondary">
              Every active teacher at the ticked schools is on this PD day's roster.
            </p>
            {institutes.length === 0 ? (
              <p className="text-sm text-content-secondary">
                No schools exist yet. Add them on the Schools page first.
              </p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {institutes.map((school) => (
                  <label
                    key={school.id}
                    className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-interactive"
                      checked={selectedSchools.includes(school.id)}
                      onChange={(event) =>
                        setSelectedSchools((current) =>
                          toggle(current, school.id, event.target.checked)
                        )
                      }
                    />
                    <span className="min-w-0 flex-1 truncate font-medium text-content-primary">
                      {school.name}
                    </span>
                    {school.is_center && (
                      <span className="rounded-full bg-interactive/10 px-2 py-0.5 text-[11px] font-semibold text-interactive">
                        Centre
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          <fieldset className="sm:col-span-2">
            <legend className="mb-2 text-sm font-medium text-content-primary">
              Facilitators (at least one)
            </legend>
            {facilitators.length === 0 ? (
              <p className="text-sm text-content-secondary">
                No facilitators exist yet. Create one on the Facilitators page first.
              </p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {facilitators.map((person) => (
                  <label
                    key={person.id}
                    className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-interactive"
                      checked={selectedFacilitators.includes(person.id || "")}
                      onChange={(event) =>
                        setSelectedFacilitators((current) =>
                          event.target.checked
                            ? [...current, person.id || ""]
                            : current.filter((id) => id !== person.id)
                        )
                      }
                    />
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-content-primary">
                        {person.name || person.assigned_id}
                      </span>
                      <span className="block truncate text-xs text-content-secondary">
                        {person.assigned_id}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          <div className="flex items-center gap-3 border-t border-border pt-5 sm:col-span-2">
            <button className={primaryButtonClass} disabled={busy}>
              {busy && <Loader2 size={16} className="animate-spin" />}
              {editing ? "Save changes" : "Schedule PD day"}
            </button>
            {editing && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-sm font-semibold text-content-secondary hover:text-content-primary"
                onClick={cancelEdit}
              >
                <X size={14} />
                Cancel
              </button>
            )}
            <span className="text-xs text-content-secondary">
              A room or facilitator already booked for an overlapping time is rejected.
            </span>
          </div>
        </form>
        {message && <InlineNotice tone={message.tone} text={message.text} breakAll />}
      </Card>

      <Card title="Scheduled PD days" text="Every planned, running and completed PD day.">
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <label className={labelClass}>
            Centre
            <select
              className={inputClass}
              value={centerID}
              onChange={(event) => setCenterID(event.target.value)}
            >
              <option value="">All centres</option>
              {centers.map((centre) => (
                <option key={centre.id} value={centre.id}>
                  {centre.name}
                </option>
              ))}
            </select>
          </label>
          <label className={labelClass}>
            Cohort
            <select
              className={inputClass}
              value={cohortID}
              onChange={(event) => setCohortID(event.target.value)}
            >
              <option value="">All cohorts</option>
              {cohorts.map((cohort) => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.name}
                </option>
              ))}
            </select>
          </label>
          <label className={labelClass}>
            Status
            <select
              className={inputClass}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">All statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In progress</option>
              <option value="ended">Ended</option>
            </select>
          </label>
        </div>

        {listError ? (
          <ErrorState message={listError} />
        ) : sessions === null ? (
          <LoadingState message="Loading PD days…" />
        ) : !sessions.length ? (
          <EmptyState
            title="No PD days scheduled"
            message="Schedule one using the form above. Teachers and facilitators see a PD day only once it is scheduled here."
          />
        ) : (
          <Table
            headers={[
              { label: "When" },
              { label: "PD day" },
              { label: "Centre", hide: "sm" },
              { label: "Room", hide: "md" },
              { label: "Schools", hide: "md" },
              { label: "Teachers" },
              { label: "Facilitators", hide: "md" },
              { label: "Status" },
              { label: "Actions" },
            ]}
            rows={sessions.map((session) => [
              <span key="when" className="whitespace-nowrap">
                {formatDate(session.start)}
                <span className="block text-xs text-content-secondary">
                  {formatTime(session.start)}
                  {" – "}
                  {formatTime(session.end)} PKT
                </span>
              </span>,
              (() => {
                const gap =
                  session.expected_teachers === 0
                    ? "No active teachers at the chosen schools — this PD day's roster is empty."
                    : null;
                return (
                  <span key="pd-day">
                    {session.pd_day}
                    <span className="block text-xs text-content-secondary">
                      {session.module_count} {session.module_count === 1 ? "module" : "modules"}
                    </span>
                    <span className="mt-1 block">
                      <AttributionCell info={attribution[session.id]} />
                    </span>
                    {gap && (
                      <span
                        className="mt-1 flex items-start gap-1 text-xs text-warning-text"
                        title={gap}
                      >
                        <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                        No roster
                      </span>
                    )}
                  </span>
                );
              })(),
              session.center,
              session.room || "—",
              session.schools.map((school) => school.name).join(", ") || "—",
              session.expected_teachers,
              session.facilitators.length
                ? session.facilitators.map((person) => person.name || person.assigned_id).join(", ")
                : "—",
              <Badge key="status" tone={statusTone[session.status] || "neutral"}>
                {session.status.replace("_", " ")}
              </Badge>,
              <span key="actions" className="flex items-center gap-2">
                <button
                  type="button"
                  className={secondaryButtonClass}
                  onClick={() => startEdit(session)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className={dangerButtonClass}
                  onClick={() => setConfirmItem(session)}
                  disabled={deletingID === session.id}
                >
                  {deletingID === session.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    "Delete"
                  )}
                </button>
              </span>,
            ])}
          />
        )}
      </Card>

      <ConfirmModal
        open={Boolean(confirmItem)}
        title="Delete this PD day?"
        message={
          confirmItem
            ? `${confirmItem.pd_day} at ${confirmItem.center} on ${formatDate(confirmItem.start)}. Attendance already recorded for it is deleted too.`
            : ""
        }
        busy={Boolean(deletingID)}
        onConfirm={() => confirmItem && remove(confirmItem)}
        onCancel={() => setConfirmItem(null)}
      />
    </div>
  );
}
